import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { AgentStatus } from "@/lib/axios/api/widgets.api";
import { useAgentPerformance } from "@/hooks/useAgentPerformance";
import { useDashboard } from "@/contexts/DashboardContext";
import { useAppData, UserRole } from "@/contexts/AppDataContext";
import { EMPTY_QUEUE_ID } from "@/constants/queue";
import { canonicalAgentStatusDisplayName, getAgentStatusDotColor } from "@/lib/agentStatusColors";
import { UserAgentOverviewTab } from "./UserAgentOverviewTab";
import { UserAgentCallsDetailTab } from "./UserAgentCallsDetailTab";

interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly userName: string | null;
  readonly agentId: string | null;
  readonly startDate: string | undefined;
  readonly endDate: string | undefined;
}

/** Background tints keyed by raw lowercase availability, paired with {@link getAgentStatusDotColor}. */
const PRESENCE_BG: Record<string, { light: string; dark: string }> = {
  available: { light: "rgba(26,159,92,.10)", dark: "rgba(26,159,92,.22)" },
  busy: { light: "rgba(199,120,0,.12)", dark: "rgba(199,120,0,.25)" },
  incall: { light: "rgba(31,105,224,.10)", dark: "rgba(31,105,224,.22)" },
  away: { light: "rgba(107,114,128,.10)", dark: "rgba(107,114,128,.30)" },
  offline: { light: "rgba(156,163,175,.10)", dark: "rgba(109,133,150,.20)" },
  "out of office": { light: "rgba(123,44,191,.10)", dark: "rgba(123,44,191,.28)" },
};

export function UserAgentDetailDialog({ open, onOpenChange, userName, agentId, startDate, endDate }: Props) {
  const [activeTab, setActiveTab] = useState<"overview" | "calls">("overview");
  const { queues, agentsStatusRaw, userRole, queueMicrosoftIds } = useAppData();
  // TCX-6500: same Manager queue-scope sentinel rule as UserAgentOverviewTab — this dialog's own
  // callIds fetch (for the Calls Detail tab count) must match the Overview tab's scope.
  const roleQueueIds = useMemo(() => {
    if (userRole !== UserRole.MANAGER) return null;
    return queueMicrosoftIds.length > 0 ? queueMicrosoftIds : [EMPTY_QUEUE_ID];
  }, [userRole, queueMicrosoftIds]);
  const { performanceData } = useAgentPerformance(agentId, startDate, endDate, roleQueueIds);
  const callIds = performanceData?.callIds ?? [];

  const { theme } = useDashboard();
  const isDark = theme === "dark";
  // agentsStatusRaw is kept fresh by AppDataContext's own 5s poll, but the popup must
  // freeze on whatever was latest at open time and NOT shift under the user while it
  // stays open — so we snapshot it into local state only on the open-transition, via
  // a ref that always tracks the current value without itself triggering the effect.
  const agentsStatusRawRef = useRef(agentsStatusRaw);
  agentsStatusRawRef.current = agentsStatusRaw;
  // Queue names resolved from queueIds get cached id->label as they're seen, additively,
  // and never cleared — AppDataContext's queues poll can transiently omit or blank a queue
  // on a single cycle (backend race), and that must not flip an already-known name into a
  // raw GUID or blank it out. Once a name is resolved, it stays until a newer real name
  // replaces it, regardless of what any single poll response contains.
  const queueNameCacheRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    queues.forEach((q) => {
      if (q.label) queueNameCacheRef.current.set(q.microsoftId, q.label);
    });
  }, [queues]);
  const [agent, setAgent] = useState<AgentStatus | undefined>(undefined);
  useEffect(() => {
    if (open) setAgent(agentsStatusRawRef.current.find((a) => a.agentId === agentId));
  }, [open, agentId]);
  const presenceKey = agent?.availability?.toLowerCase() ?? "";
  const presenceLabel = presenceKey ? canonicalAgentStatusDisplayName(presenceKey) : "";
  const presenceDotColor = getAgentStatusDotColor(presenceKey, isDark);
  const presenceBg = PRESENCE_BG[presenceKey] ?? PRESENCE_BG.offline;
  // Queue names resolved from queueIds via the call-queue list (not activeCallQueues) —
  // matches realtime-report's useAgentQueueNames so both apps show the same names.
  const queueNames = useMemo(() => {
    if (!agent?.queueIds?.length) return [];
    // Unresolved ids (cache miss) are hidden rather than shown as a raw GUID tag.
    return agent.queueIds
      .map((id) => queueNameCacheRef.current.get(id))
      .filter((name): name is string => Boolean(name));
  }, [agent, queues]);
  const tags = [agent?.directLineNumber, ...queueNames].filter(Boolean);
  const ACTIVE_TAB_CLASS = "text-[#00FEC1]";
  const INACTIVE_TAB_CLASS = "text-muted-foreground hover:text-[#00FEC1]";

  // Both tabs stay mounted; the inactive one is hidden via height:0 instead of
  // unmounted, so switching tabs never changes the dialog's height (matches
  // realtime-report's modal — Overview's measured height becomes a floor for
  // the Calls tab so the popup doesn't shrink/jump when switching).
  //
  // Uses a callback ref (not useRef + useLayoutEffect keyed on `open`) so the
  // observer attaches at the exact moment React mounts the DOM node —
  // DialogContent (Radix) only mounts its children once `open` is true, and
  // this component is created once and reused across opens/closes, so any
  // effect-timing assumption about when the ref becomes non-null is fragile.
  const [overviewMinH, setOverviewMinH] = useState<number | null>(null);
  const overviewObserverRef = useRef<ResizeObserver | null>(null);
  const overviewRef = useCallback((el: HTMLDivElement | null) => {
    overviewObserverRef.current?.disconnect();
    overviewObserverRef.current = null;
    // Reset synchronously here (not a separate effect) so a previous agent's
    // taller Overview can never leak into a new one — this runs exactly once
    // per mount, guaranteed to happen before the observer's first callback.
    setOverviewMinH(null);
    if (!el || typeof ResizeObserver === "undefined") return;
    const obs = new ResizeObserver(() => {
      const h = el.offsetHeight;
      if (h > 0) setOverviewMinH((prev) => Math.max(prev ?? 0, h));
    });
    obs.observe(el);
    overviewObserverRef.current = obs;
  }, []);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) { setActiveTab("overview"); } onOpenChange(next); }}>
      <DialogContent
        className="max-h-[calc(100vh-56px)] overflow-y-auto p-4 rounded-[16px] sm:rounded-[16px] text-foreground dark:bg-[#3C5C73] dark:border-0"
        style={{ width: "min(1180px, calc(100vw - 56px))", maxWidth: "1180px" }}
        closeClassName="absolute right-4 top-4 w-7 h-7 rounded-[7px] opacity-100 inline-grid place-items-center border border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground dark:border-white/15 dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white transition-colors"
      >
        <DialogDescription className="sr-only">Agent performance and call detail information for {userName}</DialogDescription>
        {/* Header + tabs are one self-contained block (own pb-4/border-b on each part) so
            spacing never depends on DialogContent's ambient grid `gap-4` between siblings. */}
        <div className="flex flex-col">
          <div className="flex items-start gap-4 border-b border-border dark:border-white/20 pb-4 shrink-0">
            <div className="flex-1 min-w-0">
              <div className="flex flex-col gap-[3px] min-w-0">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <DialogTitle className="text-primary dark:text-white text-[16.5px] font-semibold leading-snug">{agent?.agentName ?? userName}</DialogTitle>
                  {presenceLabel && (
                    <span
                      className="inline-flex items-center gap-[4px] rounded-full px-[8px] py-[3px] text-xs leading-none whitespace-nowrap"
                      style={{ background: isDark ? presenceBg.dark : presenceBg.light, color: presenceDotColor }}
                    >
                      <span className="w-[5px] h-[5px] rounded-full shrink-0" style={{ background: presenceDotColor }} />
                      {presenceLabel}
                    </span>
                  )}
                </div>
                {agent?.userPrincipalName && (
                  <span className="text-xs text-foreground font-normal whitespace-nowrap truncate max-w-full">
                    {agent.userPrincipalName}
                  </span>
                )}
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mt-[5px] min-w-0">
                  {tags.map((tag) => (
                    <span key={tag} className="inline-flex items-center rounded-full border border-border bg-card px-[11px] py-0.5 text-xs font-normal shrink-0 whitespace-nowrap dark:border-[#384252] dark:bg-[#0C3451] dark:text-white">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <nav className="flex border-b border-border dark:border-white/20 shrink-0 gap-4">
            <button
              className={`relative py-2 text-[14px] font-medium transition-colors focus:outline-none focus-visible:outline-none ${activeTab === "overview" ? ACTIVE_TAB_CLASS : INACTIVE_TAB_CLASS}`}
              onClick={() => setActiveTab("overview")}
            >
              Overview
              {activeTab === "overview" && (
                <span className="absolute left-0 right-0 bottom-[-1px] h-[2px] rounded-t-sm bg-brand-accent" />
              )}
            </button>
            <button
              className={`relative py-2 text-[14px] font-medium transition-colors flex items-center gap-1.5 focus:outline-none focus-visible:outline-none ${activeTab === "calls" ? ACTIVE_TAB_CLASS : INACTIVE_TAB_CLASS}`}
              onClick={() => setActiveTab("calls")}
            >
              Calls Detail{" "}
              <span className={cn(
                "text-[11px] font-semibold rounded-full border px-[7px] py-[1px] align-[1px]",
                activeTab === "calls"
                  ? "text-[#00FEC1] bg-brand-accent/10 border-[#00FEC1]/25"
                  : "text-muted-foreground bg-muted border-border dark:text-white dark:bg-[#222F44] dark:border-white/20"
              )}>
                {callIds.length}
              </span>
              {activeTab === "calls" && (
                <span className="absolute left-0 right-0 bottom-[-1px] h-[2px] rounded-t-sm bg-brand-accent" />
              )}
            </button>
          </nav>
        </div>
        <div
          className="flex flex-col"
          style={activeTab === "calls" && overviewMinH ? { height: overviewMinH } : undefined}
        >
          <div ref={overviewRef} style={activeTab !== "overview" ? { height: 0, overflow: "hidden" } : undefined}>
            <UserAgentOverviewTab agentId={agentId} startDate={startDate} endDate={endDate} queueMicrosoftIds={roleQueueIds} />
          </div>
          <div
            className={activeTab === "calls" ? "flex-1 min-h-0" : ""}
            style={activeTab !== "calls" ? { height: 0, overflow: "hidden" } : undefined}
          >
            <UserAgentCallsDetailTab callIds={callIds} userName={userName} startDate={startDate} endDate={endDate} isActive={activeTab === "calls"} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
